import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from "../../utils/api";

function ParkingValetTicket() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch parking data using the ID from URL params
  useEffect(() => {
    const fetchParkingData = async () => {
      if (!id) {
        setLoading(false);
        navigate('/accounts/parking-list'); // Go back to parking list if no ID
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });        
        console.log('API response:', response.data.data);
        
        // Store the parking data
        const parkingData = response.data.data;
        setParking(parkingData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching parking data:', err);
        setError('Failed to fetch parking data');
        setLoading(false);
        // Navigate back on error
        setTimeout(() => navigate(`/accounts/parking-print-options/${id}`), 2000);
      }
    };    
    fetchParkingData();
  }, [id, navigate]);

  useEffect(() => {
    if (!parking) return;
    
    console.log('Printing car and key tickets only:', parking);
    // Add a small delay to ensure the component is fully rendered
    setTimeout(() => {
      printCarAndKeyTicketsOnly();
    }, 500);
  }, [parking]); // Re-run when parking data changes

  const printCarAndKeyTicketsOnly = async () => {
    // Extract ticket data for printing
    const {
      ticket_number: serial = '00001',
      plate_number: plate = '',
      model = null,
      color = null,
      brand = null,
      parking_time = '',
      parking_date = '',
      parking_type = '',
      exit_location = null,
    } = parking || {};

    const modelName = model?.name || '-';
    const colorName = color?.name || '-';
    const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || '-');
    const plateDisplay = plate || '-';
    const parkingDateTime = (parking_time && parking_date) ? `${parking_date} ${parking_time}` : '-';
    const serialDisplay = serial || '-';
    const locationDisplay = exit_location?.name || '-';

    // Create ticket content for thermal printer - CAR TICKET
    const carTicketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
PARKING LOCATION:
${locationDisplay}
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'-'.repeat(32)}
CAR DETAILS:
Plate No.     : ${plateDisplay}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
Damage: ${parking.has_damage ? 'YES' : 'NO'}
${'-'.repeat(32)}
CONTACT US:
INFO@THEKEYVALET.COM
+974 3377 7441
${'-'.repeat(32)}





${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'-'.repeat(32)}
Plate No.     : ${plateDisplay}
Car Model     : ${modelName}
Color         : ${colorName}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
`;

    await androidPrintCarAndKeyTicketsOnly(carTicketContent);
  };

  const androidPrintCarAndKeyTicketsOnly = async (carTicket) => {
    try {
      const beforeUrl = 'intent:';
      const afterUrl = '#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;';
      
      // Create the print queue with appropriate delays
      const printQueue = [];
      
      // Step 1: Add car ticket to queue
      printQueue.push(() => {
        console.log('Step 1: Printing car ticket...');
        const carEncoded = encodeURIComponent(carTicket);
        const carIntentUrl = beforeUrl + carEncoded + afterUrl;
        window.location.href = carIntentUrl;
        return 1000; // Increased delay to ensure car ticket prints before key ticket
      });
    
      // Execute the print queue sequentially with delays
      const executePrintQueue = (index) => {
        if (index >= printQueue.length) {
          console.log('Car and key tickets print queue completed');
          return; // Done with the queue
        }
        
        console.log(`Executing print queue item ${index + 1} of ${printQueue.length}`);
        const printFunction = printQueue[index];
        const delay = printFunction();
        setTimeout(() => {
          executePrintQueue(index + 1);
        }, delay);
      };
      
      // Debug: Show the queue length before execution
      console.log(`Car and key tickets print queue initialized with ${printQueue.length} items`);
      
      // Start executing the print queue with a small initial delay
      setTimeout(() => {
        executePrintQueue(0);
      }, 300); // Small delay to ensure component is ready
    } catch (error) {
      console.error('Error sending car and key tickets to Android RawBT app:', error);
      // Navigate back on error
      setTimeout(() => navigate(`/accounts/parking-print-options/${id}`), 1000);
    }
  };

  // Show minimal loading/error states without complex UI
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading ticket data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <button 
          onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            fontSize: '16px'
          }}
        >
          ← Back to Print Options
        </button>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '80vh',
          fontSize: '18px',
          color: 'red'
        }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!parking) {
    return (
      <div style={{ padding: '20px' }}>
        <button 
          onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            marginBottom: '20px',
            fontSize: '16px'
          }}
        >
          ← Back to Print Options
        </button>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '80vh',
          fontSize: '18px'
        }}>
          No parking data available
        </div>
      </div>
    );
  }

  // Show minimal success state while printing
  return (
    <div style={{ padding: '20px' }}>
      <button 
        onClick={() => navigate(`/accounts/parking-print-options/${id}`)}
        style={{
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          marginBottom: '20px',
          fontSize: '16px'
        }}
      >
        ← Back to Print Options
      </button>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        fontSize: '18px',
        color: 'green'
      }}>
        Printing valet tickets...
      </div>
    </div>
  );
}

export default ParkingValetTicket;
